import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { useQueryClient } from "@tanstack/react-query";
import type { GetUserQuery } from "../API";
import { fetchEffectivePlatformLimitsByUserId } from "../graphql/fetchers";
import { queryKeys } from "../graphql/queryKeys";
import { getUser as getUserQuery } from "../graphql/queries";
import { logToConsole } from "../utilities/utilities";

type RefreshedUserProfileData = {
  firstName: string | null;
  lastName: string | null;
  nickname: string | null;
  primaryPhone: string | null;
  userIdentityId: string | null;
  userEmail: string | null;
  isUserActive: boolean | null;
};

type UserContextType = {
  user: ReturnType<typeof useAuthenticator>["user"];
  signOut: () => void | Promise<void>;
  refreshUserProfile: () => Promise<RefreshedUserProfileData | null>;
  isAuthenticated: boolean;
  isSigningOut: boolean;
  isAuthLoading: boolean;
  isUserDataLoading: boolean;
  isUserActive: boolean | null;
  route: string;
  firstName: string | null;
  lastName: string | null;
  nickname: string | null;
  primaryPhone: string | null;
  userIdentityId: string | null;
  userEmail: string | null;
};

const UserContext = createContext<UserContextType | null>(null);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, signOut, authStatus, route } = useAuthenticator((context) => [
    context.user,
    context.signOut,
    context.authStatus,
    context.route,
  ]);

  const isAuthenticated = authStatus === "authenticated";
  const isAuthLoading = authStatus === "configuring";
  const currentUserId = isAuthenticated && user?.userId ? user.userId : null;

  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [nickname, setNickname] = useState<string | null>(null);
  const [primaryPhone, setPrimaryPhone] = useState<string | null>(null);
  const [userIdentityId, setUserIdentityId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isUserActive, setIsUserActive] = useState<boolean | null>(null);
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const queryClient = useQueryClient();
  const currentUserIdRef = useRef<string | null>(currentUserId);
  currentUserIdRef.current = currentUserId;

  const isUserDataLoading =
    currentUserId !== null && resolvedUserId !== currentUserId;

  const clearUserData = useCallback(() => {
    setFirstName(null);
    setLastName(null);
    setNickname(null);
    setPrimaryPhone(null);
    setUserIdentityId(null);
    setUserEmail(null);
    setIsUserActive(null);
    setResolvedUserId(null);
  }, []);

  const refreshUserProfile = useCallback(async () => {
    const requestedUserId = user?.userId ?? null;
    if (!isAuthenticated || !requestedUserId) {
      clearUserData();
      return null;
    }

    try {
      const amplifyClient = generateClient();
      const result = await amplifyClient.graphql({
        query: getUserQuery,
        variables: { id: requestedUserId },
      });
      const { identityId } = await fetchAuthSession();
      const currentUser =
        "data" in result
          ? (result.data as GetUserQuery | undefined)?.getUser ?? null
          : null;
      const profile = currentUser?.UserProfile ?? null;

      const refreshedData: RefreshedUserProfileData = {
        firstName: profile?.firstName ?? null,
        lastName: profile?.lastName ?? null,
        nickname: profile?.preferredName ?? null,
        primaryPhone: profile?.primaryPhone ?? null,
        userIdentityId: identityId ?? null,
        userEmail: currentUser?.email ?? null,
        isUserActive:
          typeof profile?.isActive === "boolean" ? profile.isActive : null,
      };

      if (currentUserIdRef.current !== requestedUserId) {
        return null;
      }

      setFirstName(refreshedData.firstName);
      setLastName(refreshedData.lastName);
      setNickname(refreshedData.nickname);
      setPrimaryPhone(refreshedData.primaryPhone);
      setUserIdentityId(refreshedData.userIdentityId);
      setUserEmail(refreshedData.userEmail);
      setIsUserActive(refreshedData.isUserActive);
      setResolvedUserId(requestedUserId);

      return refreshedData;
    } catch (err) {
      logToConsole("error", "Failed to load user data from DynamoDB:", err);
      if (currentUserIdRef.current === requestedUserId) {
        setIsUserActive(null);
        setResolvedUserId(requestedUserId);
      }
      return null;
    }
  }, [clearUserData, isAuthenticated, user?.userId]);

  const signOutAndClearCache = useCallback(async () => {
    try {
      setIsSigningOut(true);
      queryClient.clear();
      await Promise.resolve(signOut());
    } catch (err) {
      setIsSigningOut(false);
      logToConsole("error", "Sign out failed:", err);
      queryClient.clear();
      throw err;
    }
  }, [queryClient, signOut]);

  useEffect(() => {
    if (!currentUserId) {
      clearUserData();
    }
  }, [clearUserData, currentUserId]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsSigningOut(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !currentUserId) {
      return;
    }

    void queryClient
      .prefetchQuery({
        queryKey: queryKeys.effectivePlatformLimits(currentUserId),
        queryFn: () => fetchEffectivePlatformLimitsByUserId(currentUserId),
      })
      .catch((error) => {
        logToConsole(
          "error",
          "Failed to prefetch effective platform limits:",
          error,
        );
      });
  }, [currentUserId, isAuthenticated, queryClient]);

  return (
    <UserContext.Provider
      value={{
        user,
        signOut: signOutAndClearCache,
        refreshUserProfile,
        isAuthenticated,
        isSigningOut,
        isAuthLoading,
        isUserDataLoading,
        isUserActive,
        route,
        firstName,
        lastName,
        nickname,
        primaryPhone,
        userIdentityId,
        userEmail,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within UserProvider");
  return context;
};
